import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from '../../api/axiosConfig';
import PrintableWorkOrderReport from '../printable/PrintableWorkOrderReport'; // Correct path
import LoadingSpinner from '../../components/common/LoadingSpinner';

// Define the expected structure of the fetched report data
// (Should match the structure returned by getWorkOrderReport)
interface WorkOrderReportData {
  workOrders: Array<{
    id: number;
    workOrderNumber: string;
    type: string;
    clientName: string;
    orderNumber: string;
    structureCode: string;
    structureLocation: string;
    assignedToName: string;
    status: string;
    scheduledDate: string;
    completedDate?: string | null;
    completionTime?: number | null;
  }>;
  startDate: string;
  endDate: string;
  statusFilter?: string;
  typeFilter?: string;
}

const WorkOrderReportPrintPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const startDate = searchParams.get('startDate');
  const endDate = searchParams.get('endDate');
  const status = searchParams.get('status');
  const type = searchParams.get('type');

  const [reportData, setReportData] = useState<WorkOrderReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchReportData = async () => {
      if (!startDate || !endDate) {
        setError('Не указан период для отчета.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        // Use the new backend endpoint for work order report print data
        const response = await axios.get<WorkOrderReportData['workOrders']>(`/reports/workorders/print-data`, {
          // Note: Backend returns only the array, so we construct the full object
          params: { 
            startDate, 
            endDate, 
            status: status === 'all' ? undefined : status, 
            type: type === 'all' ? undefined : type 
          } 
        });
        // Construct the full data object needed by the printable component
        setReportData({ 
          workOrders: response.data, 
          startDate, 
          endDate, 
          statusFilter: status || undefined, 
          typeFilter: type || undefined 
        });
        // Attempt to trigger print dialog after data is loaded
        setTimeout(() => {
          // window.print();
        }, 500); // Small delay
      } catch (err: any) {
        console.error('Error fetching work order report data for printing:', err);
        setError(err.response?.data?.message || 'Не удалось загрузить данные отчета для печати.');
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
  }, [startDate, endDate, status, type]); // Depend on query parameters

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <div className="printable-container error-message">Ошибка: {error}</div>;
  }

  if (!reportData) {
    return <div className="printable-container">Данные для печати не найдены.</div>;
  }

  // Render the printable component with the fetched data
  return <PrintableWorkOrderReport reportData={reportData} />;
};

export default WorkOrderReportPrintPage;
